/* FlotteSohle – TanzBegleiter
 * Single-File React-App (Phase 1). Wird per esbuild zu app.js transformiert
 * (jsx:transform → React.createElement, globales React/ReactDOM vom CDN).
 *
 * Struktur:
 *   - THEME            zentrale Farb-/Style-Tokens (dunkles Grün)
 *   - Tab-Views        HeuteView, KatalogView, TrainingView, LernenView, ProfilView
 *   - BottomNav        5 Tabs mit Emoji-Icons
 *   - App              hält den aktiven Tab, rendert Header + View + Nav
 *
 * Die Tab-Views außer "Heute" sind bewusst Platzhalter — das ist die Basis,
 * auf der die eigentlichen Features (Katalog, Training, Lernkarten) aufsetzen.
 */
const { useState } = React;

/* ---------- Theme ---------- */
const THEME = {
  bg:        '#0E1F17',   // App-Hintergrund (sehr dunkles Grün)
  surface:   '#152A20',   // Karten / Panels
  surfaceHi: '#1D3A2C',   // hervorgehobene Karten
  border:    '#264A38',
  text:      '#E7F3EC',   // Haupttext
  textDim:   '#8FB6A2',   // gedämpfter Text
  accent:    '#3FBF7F',   // Akzent-Grün (aktiv, Buttons)
  accentDim: '#2E9A63',
};

/* ---------- Wiederverwendbare Bausteine ---------- */
function Card({ children, style }) {
  return (
    <div style={{
      background: THEME.surface,
      border: `1px solid ${THEME.border}`,
      borderRadius: 16,
      padding: 16,
      ...style,
    }}>
      {children}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 style={{ fontSize: 14, fontWeight: 600, color: THEME.textDim, margin: '0 0 8px', letterSpacing: 0.3 }}>
      {children}
    </h2>
  );
}

/* Platzhalter-Karte für noch nicht gebaute Inhalte. */
function Placeholder({ icon, title, hint }) {
  return (
    <Card style={{ textAlign: 'center', padding: '32px 16px' }}>
      <div style={{ fontSize: 40, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{title}</div>
      <div style={{ color: THEME.textDim, fontSize: 14 }}>{hint}</div>
    </Card>
  );
}

/* ---------- Tab: Heute ---------- */
function HeuteView() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Willkommen */}
      <div>
        <h1 style={{ fontSize: 26, fontWeight: 700, margin: '0 0 4px' }}>Willkommen zurück 👟</h1>
        <p style={{ color: THEME.textDim, margin: 0 }}>Bereit für die nächste Runde auf dem Parkett?</p>
      </div>

      {/* Nächster Casino-Termin */}
      <div>
        <SectionTitle>NÄCHSTER CASINO-TERMIN</SectionTitle>
        <Card style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ fontSize: 32 }}>🏛️</div>
          <div>
            <div style={{ fontWeight: 600 }}>Noch kein Termin geplant</div>
            <div style={{ color: THEME.textDim, fontSize: 14 }}>Prüfungstermine erscheinen hier.</div>
          </div>
        </Card>
      </div>

      {/* Fällige Lernkarten */}
      <div>
        <SectionTitle>FÄLLIGE LERNKARTEN</SectionTitle>
        <Card style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ fontSize: 32 }}>🎴</div>
          <div>
            <div style={{ fontWeight: 600 }}>0 Karten fällig</div>
            <div style={{ color: THEME.textDim, fontSize: 14 }}>Deine Wiederholungen für heute.</div>
          </div>
        </Card>
      </div>

      {/* Quick-Button: Session loggen */}
      <button
        onClick={() => alert('Session-Logging kommt in einem der nächsten Schritte.')}
        style={{
          width: '100%',
          padding: '16px',
          background: THEME.accent,
          color: '#08130D',
          border: 'none',
          borderRadius: 14,
          fontSize: 16,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        ＋ Session loggen
      </button>
    </div>
  );
}

/* ---------- Tabs: Platzhalter ---------- */
function KatalogView() {
  return <Placeholder icon="📚" title="Katalog" hint="Tänze & Figuren – folgt in Kürze." />;
}
function TrainingView() {
  return <Placeholder icon="🏋️" title="Training" hint="Trainingssessions & Fortschritt – folgt in Kürze." />;
}
function LernenView() {
  return <Placeholder icon="🎓" title="Lernen" hint="Lernkarten & Prüfungsvorbereitung – folgt in Kürze." />;
}
function ProfilView() {
  return <Placeholder icon="👤" title="Profil" hint="Einstellungen & Statistiken – folgt in Kürze." />;
}

/* ---------- Bottom-Navigation ---------- */
const TABS = [
  { id: 'heute',    label: 'Heute',    icon: '📅', View: HeuteView },
  { id: 'katalog',  label: 'Katalog',  icon: '📚', View: KatalogView },
  { id: 'training', label: 'Training', icon: '🏋️', View: TrainingView },
  { id: 'lernen',   label: 'Lernen',   icon: '🎓', View: LernenView },
  { id: 'profil',   label: 'Profil',   icon: '👤', View: ProfilView },
];

function BottomNav({ active, onSelect }) {
  return (
    <nav
      className="safe-bottom"
      style={{
        position: 'fixed',
        left: 0, right: 0, bottom: 0,
        display: 'flex',
        background: THEME.surface,
        borderTop: `1px solid ${THEME.border}`,
        zIndex: 10,
      }}
    >
      {TABS.map((t) => {
        const isActive = t.id === active;
        return (
          <button
            key={t.id}
            onClick={() => onSelect(t.id)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
              padding: '10px 0 12px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: isActive ? THEME.accent : THEME.textDim,
            }}
            aria-label={t.label}
            aria-current={isActive ? 'page' : undefined}
          >
            <span style={{ fontSize: 22, opacity: isActive ? 1 : 0.7 }}>{t.icon}</span>
            <span style={{ fontSize: 11, fontWeight: isActive ? 600 : 500 }}>{t.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

/* ---------- App ---------- */
function App() {
  const [tab, setTab] = useState('heute');
  const Active = (TABS.find((t) => t.id === tab) || TABS[0]).View;

  return (
    <div style={{ minHeight: '100%', background: THEME.bg, color: THEME.text }}>
      {/* Header */}
      <header
        className="safe-top"
        style={{
          position: 'sticky',
          top: 0,
          background: THEME.bg,
          borderBottom: `1px solid ${THEME.border}`,
          zIndex: 5,
        }}
      >
        <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 22 }}>💃</span>
          <span style={{ fontWeight: 700, fontSize: 18 }}>FlotteSohle</span>
        </div>
      </header>

      {/* Aktiver Tab – Padding unten für die Bottom-Nav */}
      <main style={{ maxWidth: 560, margin: '0 auto', padding: '20px 16px 96px' }}>
        <Active />
      </main>

      <BottomNav active={tab} onSelect={setTab} />
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(<App />);
